/**
 * Il tasto Indietro di Android.
 *
 * ## Cosa succede senza questo file
 *
 * Nessun listener registrato significa che il guscio applica il comportamento di default: il tasto
 * fisico **chiude l'app**. Non torna alla schermata precedente, non riapre il menù: esce. Su Android
 * è il gesto che si usa più di ogni altro — è il modo in cui si annulla, si risale, si esce da una
 * scheda — e un'app che a quel gesto risponde sparendo viene percepita come rotta prima ancora che
 * come scomoda.
 *
 * ## Cosa fa
 *
 * Se c'è una pagina prima, ci torna. Se non c'è — cioè si è sulla schermata d'ingresso — **minimizza**
 * invece di uscire: l'app resta viva in secondo piano, e chi la riapre la ritrova dov'era.
 *
 * Uscire davvero non è mai la risposta giusta qui: chiuderla scarterebbe lo stato in memoria, e
 * nella coda delle timbrature può esserci una riga che aspetta la rete. Sopravvive comunque (vedi
 * `durableStore.ts`), ma non è un motivo per buttarla via.
 *
 * ## Il caso che il `canGoBack` del plugin non copre
 *
 * `canGoBack` guarda la cronologia della WebView, che non sa nulla dei nostri overlay: con un drawer
 * o una modale aperti la risposta è comunque «sì», e il tasto porterebbe alla pagina precedente
 * lasciando l'overlay per aria. Per questo un chiamante può registrare un **intercettore**: chi
 * apre un pannello dichiara come chiuderlo, e il tasto Indietro chiude quello prima di navigare.
 */
import { nativeRuntime } from '@/shared/lib/nativePlatform';

/**
 * Gli intercettori attivi, in ordine di registrazione. Si consuma **l'ultimo** — è quello sopra a
 * tutti sullo schermo, ed è quello che l'utente si aspetta di chiudere.
 */
const interceptors: Array<() => boolean> = [];

/**
 * Registra un intercettore del tasto Indietro. La funzione torna `true` se ha gestito il gesto
 * (e quindi si ferma lì), `false` per lasciarlo proseguire.
 *
 * ```ts
 * useEffect(() => (aperto ? interceptaBack(() => { chiudi(); return true; }) : undefined), [aperto]);
 * ```
 */
export function interceptaBack(handler: () => boolean): () => void {
  interceptors.push(handler);
  return () => {
    const i = interceptors.lastIndexOf(handler);
    if (i >= 0) interceptors.splice(i, 1);
  };
}

/**
 * I selettori degli overlay Mantine aperti.
 *
 * Sono i nomi di classe che Mantine 7 mette sul contenuto di drawer e modali. Guardare il DOM è
 * meno elegante che tenere un registro, ma è l'unica cosa che funziona **senza toccare i pannelli**:
 * nell'app ce ne sono dodici solo fra i mobile, ognuno con il suo `useState` locale, e nessuno di
 * loro sa di esistere in un mondo dove esiste un tasto Indietro fisico.
 */
const OVERLAY_SELECTORS = '.mantine-Drawer-content, .mantine-Modal-content';

function openOverlay(): boolean {
  if (typeof document === 'undefined') return false;
  return document.querySelector(OVERLAY_SELECTORS) !== null;
}

/**
 * Il pannello in cima si e' chiuso davvero?
 *
 * ⚠️ Questa funzione ha sostituito un `setTimeout(…, 250)`, e la ragione e' un difetto vissuto:
 * la transizione di un Drawer Mantine dura **200ms**, quindi a 250 l'elemento era **ancora nel
 * DOM** e la rete di sicurezza scattava mentre il foglio si stava chiudendo — chiudendo anche
 * l'applicazione. Cinquanta millisecondi di margine su un'animazione, su un telefono vero, non
 * sono un margine.
 *
 * ⚠️ E allungare il ritardo sarebbe stato rincorrere l'animazione: ogni transizione piu' lenta,
 * ogni telefono piu' carico, rimetterebbe il difetto. Qui non si aspetta un tempo, si **osserva
 * la rimozione**: chi si chiude viene smontato, chi rifiuta resta. Il tetto serve solo a dare un
 * verdetto a chi non si muove.
 */
function waitClosing(panel: Element, cap = 1_500): Promise<boolean> {
  if (typeof MutationObserver === 'undefined') return Promise.resolve(true);
  return new Promise((resolve) => {
    let done = false;
    const finish = (closed: boolean) => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(deadline);
      resolve(closed);
    };
    const observer = new MutationObserver(() => {
      if (!document.contains(panel)) finish(true);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const deadline = setTimeout(() => finish(!document.contains(panel)), cap);
    /* Puo' essersi gia' smontato fra il dispatch e qui. */
    if (!document.contains(panel)) finish(true);
  });
}

/**
 * Chiude l'overlay in cima simulando il tasto Esc, che è ciò che Mantine ascolta già
 * (`closeOnEscape`, attivo di default).
 *
 * Il gesto resta quello dell'utente: chi apre un bottom-sheet e preme Indietro si aspetta di
 * chiudere il foglio, non di ritrovarsi sulla pagina precedente **con il foglio ancora aperto sopra**
 * — che è quello che succedeva.
 */
function closeTopOverlay(): void {
  /*
   * ⚠️ L'evento si lancia **sull'elemento del pannello**, non su `window`, e questa riga e' la
   * correzione di un difetto che non ha mai funzionato — nemmeno nel guscio precedente.
   *
   * Mantine ascolta l'Esc su `window` (`useWindowEvent('keydown', …, { capture: true })`),
   * quindi l'evento arrivava. Ma dentro al suo gestore c'e':
   *
   *     event.target?.getAttribute('data-mantine-stop-propagation') !== 'true'
   *
   * e lanciando su `window` il `target` **e' `window`**, che non ha `getAttribute`: l'optional
   * chain guarda se `target` e' nullo, non se il metodo esiste, quindi la chiamata **lancia un
   * TypeError** e `onClose()` non viene mai raggiunto. Identico su `document`.
   *
   * Su un elemento il `target` ha `getAttribute`, che risponde `null`, e la chiusura parte.
   * L'evento **risale** comunque a `window` grazie a `bubbles: true`, quindi il gestore di
   * Mantine lo riceve come se l'utente avesse premuto Esc col fuoco dentro al pannello — che e'
   * esattamente cio' che si vuole simulare.
   *
   * ⚠️ Trovato provando il tasto Indietro su un telefono: il foglio delle azioni non si
   * chiudeva, e non c'era nessun errore visibile perche' il TypeError muore dentro al gestore
   * di un listener di libreria.
   */
  const pannello = typeof document === 'undefined' ? null : document.querySelector(OVERLAY_SELECTORS);
  const event = new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true });
  if (pannello) pannello.dispatchEvent(event);
  else window.dispatchEvent(event);
}

/** Esposta per i test: applica la politica senza passare dal plugin. */
export function manageBack(canGoBack: boolean, actions: {
  back: () => void;
  minimize: () => void;
}): 'intercettato' | 'overlay' | 'indietro' | 'minimizzato' {
  for (let i = interceptors.length - 1; i >= 0; i -= 1) {
    if (interceptors[i]()) return 'intercettato';
  }

  /* Gli overlay vengono prima della navigazione: sono ciò che l'utente ha davanti agli occhi.
     Un pannello che rifiutasse l'Esc (`closeOnEscape={false}`) lascerebbe il tasto senza effetto,
     e per non lasciarlo *bloccato* chi chiama ricontrolla poco dopo — vedi `initNativeBack`. */
  if (openOverlay()) { closeTopOverlay(); return 'overlay'; }

  if (canGoBack) { actions.back(); return 'indietro'; }
  actions.minimize();
  return 'minimizzato';
}

/**
 * Le opzioni dell'aggancio.
 *
 * ⚠️ `puoTornare` si **inietta** invece di essere letto qui, e la ragione non e' l'eleganza:
 * l'unico che sa davvero se c'e' una tappa precedente e' il router, e importarlo dentro
 * `shared/lib` rovescerebbe la dipendenza (la libreria condivisa che chiama l'applicazione).
 * Chi aggancia il tasto conosce gia' il router: glielo passa.
 */
export interface BackOptions {
  /** `true` se esiste una tappa precedente. Senza, si ripiega su `history.length`. */
  canGoBack?: () => boolean;
  /**
   * Cosa fare quando non c'e' piu' dove tornare.
   *
   * ⚠️ Senza, si esce **subito**: l'app va in secondo piano e per chi guarda lo schermo e'
   * un'uscita. Con questa, si puo' chiedere prima — che e' il comportamento di Lares
   * (`chiediSeUscire` in `utility/native/utils-back-button.js`) e quello che un utente si
   * aspetta dopo aver premuto Indietro sulla home per sbaglio.
   *
   * ⚠️ La domanda **non** vive qui: questo file non conosce l'interfaccia, e un modale dentro
   * `shared/lib` legherebbe il tasto Indietro alla libreria dei componenti. Chi aggancia il
   * tasto passa la funzione; qui si sa solo che al posto di uscire si chiama lei.
   */
  onExit?: (exit: () => void) => void;
}

/**
 * Aggancia il tasto Indietro. Da chiamare una volta all'avvio.
 *
 * ⚠️ Un ramo solo: qui c'era anche quello del guscio precedente, con la sua rete di sicurezza
 * gemella su `attendiChiusura`. Non e' andato perduto niente — la stessa logica vive in
 * `agganciaBackCordova`, che e' l'unica che gira nell'APK — ma vale ricordare che il ramo
 * l'altro era **quello scritto per primo**, e il difetto dell'Esc su `window` (vedi
 * `chiudiOverlayInCima`) era rotto in tutti e due.
 */
export function initNativeBack(options: BackOptions = {}): void {
  if (nativeRuntime() !== 'cordova') return;
  hookBackCordova(options);
}

/** Solo per i test: svuota gli intercettori registrati. */
export function resetInterceptors(): void {
  interceptors.length = 0;
}

/* ───────────────────────────── il ramo Cordova ───────────────────────────── */

/**
 * Il tasto Indietro di Android, col guscio Cordova.
 *
 * ⚠️ Senza questo, nell'APK il tasto Indietro **non era agganciato affatto**: `isNative()`
 * interrogava il guscio precedente, che li' non esiste, quindi `initNativeBack` usciva subito. Il
 * risultato non e' «il tasto non fa niente» — e' peggio: la WebView applica il suo
 * comportamento predefinito e **chiude l'applicazione**, anche con un pannello aperto. Il
 * difetto piu' silenzioso di questa serie, perche' sembra una scelta di prodotto.
 *
 * ⚠️ Due differenze dal ramo precedente, e sono dichiarate perche' non sono traducibili:
 *
 * · **`canGoBack` non esiste.** L'evento `backbutton` di Cordova non porta niente. Si
 *   approssima con `history.length > 1`, ed e' un'approssimazione vera: `history.length` conta
 *   anche voci che non sono nostre, ed e' la ragione per cui Lares si tiene una scia propria
 *   delle rotte (`utility/native/utils-back-button.js`). Qui la scia non c'e' perche' la
 *   decisione la prende `gestisciBack`, che vuole solo un booleano: se un giorno il tasto
 *   sbagliera' l'ultima tappa, il posto da guardare e' questa riga.
 *
 * · **«minimizza» non esiste.** L'altro guscio aveva un `minimizeApp()`, che manda l'app in secondo
 *   piano; Cordova ha `navigator.app.exitApp()`, che chiude l'activity. Per chi guarda lo
 *   schermo sono la stessa cosa — si torna al launcher — ma non lo sono per il sistema, e
 *   chiamarle uguali in un commento sarebbe una bugia comoda.
 */
interface AppCordova {
  exitApp?: () => void;
}

function hookBackCordova(opzioni: BackOptions = {}): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('backbutton', (evento) => {
    /* Cordova non lo annulla da se': senza questo, dopo il nostro gestore parte anche il
       comportamento predefinito e l'app si chiude comunque. */
    evento.preventDefault();

    /*
     * ⚠️ Il segnale viene dal **router**, quando c'e'. `history.length > 1` era
     * un'approssimazione dichiarata e sbagliava proprio il caso che conta: dopo l'accesso e la
     * normalizzazione dell'indirizzo quel numero e' quasi sempre maggiore di uno, quindi dalla
     * home il tasto Indietro faceva `history.back()` invece di uscire — e visivamente **non
     * succedeva niente**, perche' si tornava a una vista equivalente.
     *
     * `router.history.canGoBack()` conta le tappe **del router**, che sono quelle vere.
     */
    const canGoBack = opzioni.canGoBack
      ? opzioni.canGoBack()
      : typeof window !== 'undefined' && window.history.length > 1;
    const back = () => window.history.back();
    const minimize = () => {
      const app = (window as { navigator?: { app?: AppCordova } }).navigator?.app;
      app?.exitApp?.();
    };

    /* Il pannello si prende **prima**: `gestisciBack` lo chiude, e dopo non c'e' piu' niente
       da osservare. */
    const topPanel = typeof document === 'undefined'
      ? null
      : document.querySelector(OVERLAY_SELECTORS);
    const uscita = opzioni.onExit
      ? () => { opzioni.onExit?.(minimize); }
      : minimize;
    const outcome = manageBack(canGoBack, { back, minimize: uscita });

    /* La stessa rete di sicurezza del ramo precedente, e per la stessa ragione: se l'Esc non ha
       chiuso niente, il tasto Indietro sembrerebbe rotto. */
    /*
     * Rete di sicurezza: se l'Esc non ha chiuso niente — un pannello con `closeOnEscape={false}`,
     * o un overlay non-Mantine — il tasto Indietro sembrerebbe rotto, e si naviga come sarebbe
     * successo senza di noi.
     *
     * ⚠️ Si **osserva** la rimozione invece di aspettare un tempo: vedi `attendiChiusura`. Con un
     * ritardo fisso la rete scattava durante l'animazione di chiusura e chiudeva l'applicazione
     * insieme al foglio.
     */
    if (outcome === 'overlay' && topPanel) {
      void waitClosing(topPanel).then((chiuso) => {
        if (chiuso) return;
        if (canGoBack) back();
        else uscita();
      });
    }
  }, false);
}
